import React, { Component, Fragment } from "react";
import { Modal } from "assets/scripts/components/presentational/Modal";
import uid from "assets/scripts/utils/uid";
import Form from "assets/scripts/components/common/form/Form/Form";
import { REQUIRED } from "assets/scripts/constants/FormMessages";
import { defineMessages, FormattedMessage, injectIntl } from "react-intl";
import SelectFormGroup from "assets/scripts/components/common/form/SelectFormGroup/SelectFormGroup";
import getApiMessage from "assets/scripts/utils/getApiMessage";
import Dropzone from "react-dropzone";
import FormGroup from "assets/scripts/components/common/form/FormGroup/FormGroup";
import createRef from "assets/scripts/utils/createRef";
import styles from "./AddDocumentModal.module.less";
import classnames from "classnames";
import WithAccounts, { AccountLoadStatus } from "assets/scripts/components/containers/secure/WithAccounts/WithAccounts";
import AccountFormGroups from "assets/scripts/components/common/form/AccountFormGroups/AccountFormGroups";
import { ASYNC_STATE } from "assets/scripts/constants/Constants";
import StatusMessage from "assets/scripts/components/presentational/StatusMessage";
const FileFormGroup = FormGroup;
const messages = defineMessages({
    submit: {
        id: "document.addDocumentModal.submit",
        defaultMessage: "Upload"
    },
    documentTypeIdLabel: {
        id: "document.addDocumentModal.documentTypeId.label",
        defaultMessage: "Document Type"
    },
    fileLabel: {
        id: "document.addDocumentModal.file.label",
        defaultMessage: "File"
    },
    fileError: {
        id: "document.addDocumentModal.file.error",
        defaultMessage: "Invalid file type"
    },
    acctsLabel: {
        id: "document.addDocumentModal.accts.label",
        defaultMessage: "Related Accounts"
    },
    uploadError: {
        id: "documents.addDocumentModal.uploadError",
        defaultMessage: "Your document could not be uploaded due to this error:"
    },
    removeFile: {
        id: "documents.addDocumentModal.removeFile",
        defaultMessage: "Remove file"
    },
    fileSizeError: {
        id: "documents.addDocumentModal.file.sizeError",
        defaultMessage: "Files must be smaller than 8MB"
    }
});
const EMPTY_DOCUMENT_TYPE_OPTION = {
    value: "-1",
    label: ""
};
const INITIAL_STATE = {
    documentTypeId: EMPTY_DOCUMENT_TYPE_OPTION.value,
    file: null,
    accts: {}
};
const ALLOWED_TYPES = [
    "jpg",
    "jpeg",
    "gif",
    "png",
    "pdf",
    "tif",
    "tiff",
    "doc",
    "docx",
    "txt",
    "csv"
];
function getExtension(fileName) {
    return (fileName.split(".").pop() || "").toLowerCase();
}
function getFileError(file) {
    if (file == null) {
        return REQUIRED;
    }
    const ext = getExtension(file.name);
    if (ALLOWED_TYPES.indexOf(ext) === -1) {
        return messages.fileError;
    }
    if (file.size > (8 * 1024 * 1024)) {
        return messages.fileSizeError;
    }
    return null;
}
function getFileIcon(fileName) {
    switch (getExtension(fileName)) {
        case "jpg":
        case "jpeg":
        case "gif":
        case "png":
        case "tif":
        case "tiff":
            return "fa-file-image-o";
        case "pdf":
            return "fa-file-pdf-o";
        case "doc":
        case "docx":
            return "fa-file-word-o";
        case "xls":
        case "xlsx":
            return "fa-file-excel-o";
        case "txt":
        case "csv":
        default:
            return "fa-file-o";
    }
}
const WithoutAccounts = ({ children }) => {
    const data = {
        accts: {
            live: [],
            deceased: []
        },
        error: null,
        loading: false
    };
    return children(data);
};
class AddDocumentModal extends Component {
    constructor() {
        super(...arguments);
        this._uid = `add-document-${uid()}`;
        this._dropzoneRef = createRef();
        this._fileFormGroupRef = createRef();
        this.state = INITIAL_STATE;
        this._handleDocumentTypeIdChange = (documentTypeId) => this.setState({ documentTypeId });
        this._handleFile = (accepted, rejected) => {
            this._fileFormGroupRef.value.touch();
            this.setState({
                file: accepted[0] || rejected[0] || null
            });
        };
        this._handleAcctsChange = (accts) => {
            this.setState({ accts });
        };
        this._handleBrowseClick = () => {
            this._dropzoneRef.value.open();
        };
        this._handleRemoveFileClick = () => {
            this._fileFormGroupRef.value.untouch();
            this.setState({ file: null });
        };
        this._handleClose = () => this.props.onClose();
        this._handleSubmit = () => {
            const { state } = this;
            const { accts } = state;
            const payableIds = Object.keys(accts)
                .map(id => parseInt(id, 10))
                .filter(id => accts[id]);
            this.props.onUpload({
                documentTypeId: parseInt(state.documentTypeId, 10),
                fileName: state.file.name,
                file: state.file,
                payableIds
            });
        };
    }
    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) {
            this.setState(INITIAL_STATE);
        }
    }
    render() {
        const { props } = this;
        const fileInputId = `input-${this._uid}`;
        const AcctProvider = !!this.props.withoutAccounts ? WithoutAccounts : WithAccounts;
        return (<Modal id={this._uid} isActiveModal={props.open} modalId={this._uid} triggerId={props.triggerId} closeModal={this._handleClose}>
				<Dropzone ref={this._dropzoneRef} onDrop={this._handleFile} style={{}} multiple={false} activeClassName={styles.dragging} inputProps={{ id: fileInputId }} accept={ALLOWED_TYPES.map(it => `.${it}`).join(",")} disableClick disablePreview>
					<div className="vr vr_x5">
						<h1 className="hdg">
							<FormattedMessage id="document.addDocumentModal.header" defaultMessage="Upload Document"/>
						</h1>
					</div>

					<div className="vr vr_x5">
						<div className="text">
							<FormattedMessage tagName="p" id="document.addDocumentModal.allowedTypes" defaultMessage="We accept the following file types: {types}" values={{
            types: ALLOWED_TYPES
                .map(it => `.${it.toUpperCase()}`)
                .join(", ")
        }}/>
						</div>
					</div>

					<AcctProvider>
						{({ loading, error, accts }) => (<Fragment>
								<AccountLoadStatus loading={loading} error={error}/>

								<StatusMessage isError isActive={props.error != null} error={props.error} customMessage={messages.uploadError}/>

								{!loading && error == null && (this._renderForm(fileInputId, accts))}
							</Fragment>)}
					</AcctProvider>
				</Dropzone>
			</Modal>);
    }
    _renderForm(fileInputId, accts) {
        const { props, state } = this;
        const emptyDocType = state.documentTypeId === EMPTY_DOCUMENT_TYPE_OPTION.value;
        const errors = {
            documentTypedId: emptyDocType ? REQUIRED : null,
            file: getFileError(state.file)
        };
        const { globalError, backendErrors } = props.backendErrors;
        const isValid = Object.values(errors).every(it => it == null);
        const documentTypeOptions = props.documentTypes.filter(it => it.userFlag).map(type => ({
            value: type.id.toString(),
            label: getApiMessage(props.locale, type.name, type.nameES)
        }));
        if (emptyDocType) {
            documentTypeOptions.unshift(EMPTY_DOCUMENT_TYPE_OPTION);
        }
        const routeGroups = [...accts.deceased, ...accts.live];
        return (<Fragment>
				<Form className="vr vr_x4" isBusy={props.state === ASYNC_STATE.PENDING} isValid={isValid} error={globalError} onSubmit={this._handleSubmit} submitLabel={messages.submit}>
					<SelectFormGroup name="documentTypeId" label={messages.documentTypeIdLabel} value={state.documentTypeId} options={documentTypeOptions} onChange={this._handleDocumentTypeIdChange} error={errors.documentTypedId} backendError={backendErrors.documentTypeId} required/>

					<FileFormGroup ref={this._fileFormGroupRef} id={fileInputId} name="file" label={messages.fileLabel} value={state.file} error={errors.file} backendError={backendErrors.file || backendErrors.fileName} required>
						{({ value, attrs }) => {
            return (<div className={classnames(styles.fileInput, {
                [styles.noFile]: state.file == null,
                [styles.disabled]: attrs.disabled
            })}>
									
									<i className="fa fa-file-o u-isVisuallyHidden" aria-hidden="true"/>
									{value == null && !attrs.disabled && (<span>
											<FormattedMessage id="document.addDocumentModal.file.empty" defaultMessage="Drop a file here or {browse}" values={{
                browse: (<button type="button" className="link" onClick={this._handleBrowseClick}>
															<FormattedMessage id="document.addDocumentModal.file.empty.browse" defaultMessage="browse your computer"/>
														</button>)
            }}/>
										</span>)}
									{value == null && attrs.disabled && (<span>&nbsp;</span>)}
									{value != null && (<span>
											<i className={classnames("fa", styles.icon, getFileIcon(value.name))}/>
											{" " + value.name}
											{!attrs.disabled && (<button className={styles.clearBtn} type="button" title={props.intl.formatMessage(messages.removeFile)} onClick={this._handleRemoveFileClick}>
													<i className="fa fa-times"/>
													<span className="u-isVisuallyHidden">
														<FormattedMessage {...messages.removeFile}/>
													</span>
												</button>)}
										</span>)}
								</div>);
        }}
					</FileFormGroup>

					{!props.withoutAccounts && (<AccountFormGroups className={styles.relatedAccts} label={messages.acctsLabel} routeGroups={routeGroups} selectedIds={state.accts} onChange={this._handleAcctsChange}/>)}
				</Form>

				<StatusMessage isLoad isActive={props.state === ASYNC_STATE.PENDING}/>
			</Fragment>);
    }
}
export default injectIntl(AddDocumentModal);



// WEBPACK FOOTER //
// ./src/assets/scripts/components/containers/secure/Documents/AddDocumentModal/AddDocumentModal.tsx